'use client'
import classNames from 'classnames'
import Link from 'next/link'
import type { FC, MouseEvent } from 'react'
import React from 'react'
import { useState } from 'react'
import { AddressPickerMobile } from '../address-picker-mobile/AddressPickerMobile'
import type { HeaderColorType } from '../Header'
import { UserButton } from '../user-button/UserButton'
import styles from './HeaderMobile.module.scss'
import { useDeliveryAddress } from '@/delivery-address/client'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling } from '@/tools/o11y/client'
import {
  ArrowForwardIcon,
  LocationMarkerIcon,
  LogoSize,
  LogoSurface,
  LogoVariant,
  MainLogo,
  TextInput,
} from '@/tools/ui'
import { UserSearchInput } from '@/tools/ui/components/header/ui/user-search-input/UserSearchInput'
import { AddressBookModal } from '@/user/address-book/ui/AddressBookModal'

interface Props {
  className?: string
  shouldShowAddress?: boolean
  color?: HeaderColorType
  shouldShowSearch?: boolean
  searchType: string
  isIntersecting: boolean
}

export const HeaderMobile: FC<Props> = ({
  className,
  shouldShowAddress,
  shouldShowSearch,
  searchType,
  color,
  isIntersecting,
}) => {
  const { getLanguage, t } = useLocalisation()
  const { withErrorTracking } = useErrorHandling()

  const language = getLanguage()
  const { deliveryAddress } = useDeliveryAddress()
  const [isOpenAddressBook, setIsOpenAddressBook] = useState(false)
  const isBrandHeader = color === 'brand'
  const shouldSHowAddressInput = (isIntersecting && isBrandHeader) || !isBrandHeader
  const handleOpenAddressBook = withErrorTracking(() => {
    setIsOpenAddressBook(true)
  })

  const handleCloseAddressBook = withErrorTracking(() => {
    setIsOpenAddressBook(false)
  })

  const handleSearchClick = withErrorTracking((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
  })

  return (
    <div
      className={classNames({
        [styles.scrolled]: isIntersecting,
        [styles.headerMobile]: true,
        [className ?? '']: true,
        [styles.headerMobileWithAddress]:
          shouldShowAddress && deliveryAddress?.text && !shouldShowSearch,
      })}
    >
      <section className={styles.topContainer}>
        <div className={styles.leftContent}>
          <Link className={styles.logoLink} href={`/${language}`}>
            <MainLogo
              className={styles.logoImage}
              size={LogoSize.small}
              surface={LogoSurface.onBrand}
              variant={LogoVariant.logotype}
            />
          </Link>
        </div>
        <div>
          <UserButton color={color} isIntersecting={isIntersecting} isMobile={true} />
        </div>
      </section>
      <div className={styles.wrapper} role="button" onClick={handleOpenAddressBook}>
        {shouldShowAddress ? (
          deliveryAddress?.text ? (
            <>
              <AddressPickerMobile addressText={deliveryAddress.text} />
              {shouldShowSearch ? (
                <div onClick={handleSearchClick}>
                  <UserSearchInput searchType={searchType} />
                </div>
              ) : null}
            </>
          ) : (
            shouldSHowAddressInput && (
              <TextInput
                readonly
                aria-label={t('address-list.search-address')}
                leadingIcon={LocationMarkerIcon}
                placeholder={t('landing.address-picker')}
                trailingIcon={ArrowForwardIcon}
              />
            )
          )
        ) : null}
      </div>
      <AddressBookModal
        isOpen={isOpenAddressBook}
        onClose={handleCloseAddressBook}
        onSelect={handleCloseAddressBook}
      />
    </div>
  )
}
